import React, { useContext, useEffect, useMemo, useState } from "react";
import { Badge, Button, Form, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import api from "../../api";
import { AuthContext } from "../../context/AuthContext";
import { LoadingState } from "../../components/ui/LoadingState";
import { ErrorState } from "../../components/ui/ErrorState";
import "./ProjectWorkspace.css";

const WORK_TYPES = [
  "DEVELOPMENT", "TESTING", "SUPPORT", "ANALYSIS",
  "DOCUMENTATION", "REVIEW", "DEPLOYMENT", "COORDINATION", "OTHER",
];

const newEntry = () => ({
  taskId: "",
  work_date: dayjs().format("YYYY-MM-DD"),
  work_description: "",
  effort_minutes: "",
  work_type: "DEVELOPMENT",
});

function readableMinutes(minutes) {
  const total = Number(minutes) || 0;
  return `${Math.floor(total / 60)}h ${total % 60}m`;
}

// This tab reads factual records from task_performer_work_logs, not task
// descriptions or a synthetic estimate of work derived from due dates.
// Creating an entry uses the existing task-performer work-log API; that
// API enforces that the author is an explicitly active task performer.
export default function ProjectWorkspaceDailyWorkTab({ projectId }) {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext) || {};
  const basePath = `/${user?.role || "admin"}`;
  const [filter, setFilter] = useState({ user_id: "", from_date: "", to_date: "" });
  const [page, setPage] = useState(1);
  const [refreshKey, setRefreshKey] = useState(0);
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // The server answered 404 for the work-log route itself: this backend
  // build does not include Daily Work yet (not a data or permission issue).
  const [unavailable, setUnavailable] = useState(false);
  const [entry, setEntry] = useState(newEntry);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let current = true;
    setLoading(true);
    setError("");
    setUnavailable(false);
    api.get(`/projects/${projectId}/work-logs`, {
      params: {
        page,
        pageSize: 50,
        ...(filter.user_id ? { user_id: filter.user_id } : {}),
        ...(filter.from_date ? { from_date: filter.from_date } : {}),
        ...(filter.to_date ? { to_date: filter.to_date } : {}),
      },
    })
      .then((result) => { if (current) setResponse(result.data?.data || null); })
      .catch((err) => {
        if (!current) return;
        if (err.response?.status === 404) setUnavailable(true);
        setError(err.response?.data?.message || "Unable to load daily work.");
      })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [projectId, page, filter, refreshKey]);

  const logs = Array.isArray(response?.logs) ? response.logs : [];
  const members = Array.isArray(response?.members) ? response.members : [];
  const availableTasks = Array.isArray(response?.availableTasks) ? response.availableTasks : [];
  const total = Number(response?.total) || 0;
  const pageSize = Number(response?.pageSize) || 50;
  const pages = Math.max(1, Math.ceil(total / pageSize));

  const grouped = useMemo(() => {
    const result = [];
    for (const log of logs) {
      const date = log.work_date || "Unspecified date";
      if (!result.length || result[result.length - 1].date !== date) {
        result.push({ date, entries: [], minutes: 0 });
      }
      const group = result[result.length - 1];
      group.entries.push(log);
      group.minutes += Number(log.effort_minutes) || 0;
    }
    return result;
  }, [logs]);
  const visibleMinutes = grouped.reduce((sum, group) => sum + group.minutes, 0);

  const updateFilter = (field, value) => {
    setPage(1);
    setFilter((prev) => ({ ...prev, [field]: value }));
  };

  const submitWork = async (event) => {
    event.preventDefault();
    const minutes = Number(entry.effort_minutes);
    if (!entry.taskId || !entry.work_date || !entry.work_description.trim()
      || !Number.isSafeInteger(minutes) || minutes < 1) {
      toast.warning("Select a task and enter the work date, description and positive effort in minutes.");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/tasks/${entry.taskId}/work-logs`, {
        work_date: entry.work_date,
        work_description: entry.work_description.trim(),
        effort_minutes: minutes,
        work_type: entry.work_type,
        status: "RECORDED",
      });
      toast.success("Daily work recorded");
      setEntry(newEntry());
      setRefreshKey((prev) => prev + 1);
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not record daily work");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="pw-daily-work" aria-label="Project daily work progress">
      <div className="pw-daily-heading">
        <div>
          <h5>Daily Work Progress</h5>
          <p className="text-muted small mb-0">
            Actual development, testing and deployment activity recorded by project task performers.
            Completed tasks without a work log are not treated as a daily entry.
          </p>
        </div>
        <Button variant="outline-secondary" size="sm" onClick={() => setRefreshKey((prev) => prev + 1)}>
          Refresh
        </Button>
      </div>

      {unavailable ? (
        <div className="pw-card pw-daily-unavailable" role="status">
          <div className="pw-daily-unavailable-icon" aria-hidden="true"><i className="fa-solid fa-screwdriver-wrench"></i></div>
          <div>
            <h6>Daily Work is not available on this server yet</h6>
            <p className="small text-muted mb-2">
              The server connected to this page does not have the Daily Work update installed, so work logs
              cannot be loaded or recorded here. No data is missing — this section will work once the
              backend update is deployed.
            </p>
            <Button variant="outline-secondary" size="sm" onClick={() => setRefreshKey((prev) => prev + 1)}>Check again</Button>
          </div>
        </div>
      ) : (
      <>
      <div className="pw-card">
        <h6>Record today's work</h6>
        {loading ? (
          <div className="small text-muted">Loading your tasks…</div>
        ) : error ? (
          <div className="small text-muted">Your performer tasks could not be loaded. Use Retry below.</div>
        ) : availableTasks.length === 0 ? (
          <div className="small text-muted">
            No tasks where you are an active performer. A project manager can add you as a performer
            on the relevant task before you record work. Being only the task assignee is not enough
            for the existing work-log API.
          </div>
        ) : (
          <Form onSubmit={submitWork}>
            <div className="pw-daily-form-grid">
              <Form.Group controlId="pw-daily-task">
                <Form.Label>Task</Form.Label>
                <Form.Select required value={entry.taskId} onChange={(e) => setEntry((p) => ({ ...p, taskId: e.target.value }))}>
                  <option value="">Select your performer task</option>
                  {availableTasks.map((task) => <option key={task.id} value={task.id}>#{task.id} – {task.title}</option>)}
                </Form.Select>
              </Form.Group>
              <Form.Group controlId="pw-daily-date">
                <Form.Label>Work date</Form.Label>
                <Form.Control required type="date" value={entry.work_date} onChange={(e) => setEntry((p) => ({ ...p, work_date: e.target.value }))} />
              </Form.Group>
              <Form.Group controlId="pw-daily-effort">
                <Form.Label>Effort (minutes)</Form.Label>
                <Form.Control required type="number" min="1" step="1" value={entry.effort_minutes} onChange={(e) => setEntry((p) => ({ ...p, effort_minutes: e.target.value }))} />
              </Form.Group>
              <Form.Group controlId="pw-daily-type">
                <Form.Label>Work type</Form.Label>
                <Form.Select value={entry.work_type} onChange={(e) => setEntry((p) => ({ ...p, work_type: e.target.value }))}>
                  {WORK_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
                </Form.Select>
              </Form.Group>
            </div>
            <Form.Group controlId="pw-daily-description" className="mt-3">
              <Form.Label>What did you work on?</Form.Label>
              <Form.Control as="textarea" rows={2} required maxLength={1000} value={entry.work_description}
                placeholder="Example: Implemented teacher attendance API and verified response handling."
                onChange={(e) => setEntry((p) => ({ ...p, work_description: e.target.value }))} />
            </Form.Group>
            <div className="mt-3">
              <Button type="submit" disabled={saving} className="unit-btn unit-btn-primary">
                {saving ? <><Spinner size="sm" animation="border" className="me-2" />Saving…</> : "Save Daily Update"}
              </Button>
            </div>
          </Form>
        )}
      </div>

      <div className="pw-card">
        <div className="pw-daily-heading">
          <h6>Recorded work</h6>
          <span className="text-muted small">{total} entries · {readableMinutes(visibleMinutes)} on this page</span>
        </div>
        <div className="pw-daily-filters">
          <Form.Group controlId="pw-daily-filter-member">
            <Form.Label>Developer / tester</Form.Label>
            <Form.Select value={filter.user_id} onChange={(e) => updateFilter("user_id", e.target.value)}>
              <option value="">All team members</option>
              {members.map((member) => <option key={member.id} value={member.id}>{member.name}</option>)}
            </Form.Select>
          </Form.Group>
          <Form.Group controlId="pw-daily-filter-from">
            <Form.Label>From</Form.Label>
            <Form.Control type="date" value={filter.from_date} onChange={(e) => updateFilter("from_date", e.target.value)} />
          </Form.Group>
          <Form.Group controlId="pw-daily-filter-to">
            <Form.Label>To</Form.Label>
            <Form.Control type="date" value={filter.to_date} onChange={(e) => updateFilter("to_date", e.target.value)} />
          </Form.Group>
        </div>

        {loading ? <LoadingState message="Loading daily work…" /> : error ? (
          <ErrorState message={error} action={<Button onClick={() => setRefreshKey((prev) => prev + 1)}>Retry</Button>} />
        ) : grouped.length === 0 ? (
          <p className="text-muted small mb-0">No recorded work found for these filters. Task completion does not create a work log automatically.</p>
        ) : (
          <>
            {grouped.map((group) => (
              <div key={group.date} className="pw-daily-day">
                <div className="pw-daily-day-header">
                  <strong>{dayjs(group.date).isValid() ? dayjs(group.date).format("DD MMM YYYY") : group.date}</strong>
                  <span className="text-muted small">{group.entries.length} entries · {readableMinutes(group.minutes)}</span>
                </div>
                <div className="pw-table-wrap">
                  <table className="pw-table">
                    <thead><tr><th>Member</th><th>Task / milestone</th><th>Work done</th><th>Type</th><th>Effort</th></tr></thead>
                    <tbody>
                      {group.entries.map((log) => (
                        <tr key={log.id}>
                          <td>{log.user_name}</td>
                          <td>
                            <button className="pw-task-link" type="button" onClick={() => navigate(`${basePath}/tasks/${log.task_id}`)}>
                              #{log.task_id} {log.task_title}
                            </button>
                            {log.milestone_name && <div className="small text-muted">{log.milestone_name}</div>}
                            {log.phase_name && <div className="small text-muted">{log.phase_name}</div>}
                          </td>
                          <td>
                            <div className="pw-daily-description">{log.work_description || "No description recorded"}</div>
                            {log.status === "DRAFT" && <Badge bg="secondary">Draft</Badge>}
                          </td>
                          <td>{log.work_type || "OTHER"}</td>
                          <td>{readableMinutes(log.effort_minutes)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
            <div className="pw-daily-pagination">
              <span className="small text-muted">Page {page} of {pages}</span>
              <Button variant="outline-secondary" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
              <Button variant="outline-secondary" size="sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
            </div>
          </>
        )}
      </div>
      </>
      )}
    </section>
  );
}
